import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import maplibregl from 'maplibre-gl';
import { formatStepDistance } from '../../utils/routeParser';

/**
 * RouteStepMarkers - Renders interactive step indicator dots on the SELECTED route only.
 * 
 * Uses a GeoJSON circle layer (GPU-rendered) so dots stay perfectly fixed
 * on the route during pan/zoom. Only shows dots on the currently selected route.
 * 
 * Exposes highlightStep(index) and clearHighlight() via ref for external control
 * (e.g., hovering a step in the routing details panel).
 */

const SOURCE_ID = 'route-step-markers-source';
const LAYER_ID = 'route-step-markers-layer';

const RouteStepMarkers = forwardRef(({ mapRef, routeDetails, selectedRouteIndex = 0 }, ref) => {
  const popupRef = useRef(null);
  const handlersRef = useRef(null);
  const hoveredIdRef = useRef(null);
  const pointsRef = useRef([]);

  /**
   * Get the active route's geometry and legs based on selectedRouteIndex.
   */
  const getActiveRoute = useCallback(() => {
    if (!routeDetails) return null;

    if (selectedRouteIndex === 0) {
      return {
        geometry: routeDetails.geometry,
        legs: routeDetails.legs,
      };
    }

    const alternates = routeDetails.alternateRoutes || [];
    const altIndex = selectedRouteIndex - 1;
    if (altIndex >= 0 && altIndex < alternates.length) {
      const alt = alternates[altIndex];
      return {
        geometry: alt.geometry,
        legs: alt.legs || [],
      };
    }

    return null;
  }, [routeDetails, selectedRouteIndex]);

  /**
   * Extract maneuver points with coordinates from the active route.
   * Returns only non-depart/non-arrive maneuvers (matching what the details panel shows).
   */
  const getManeuverPoints = useCallback(() => {
    const activeRoute = getActiveRoute();
    if (!activeRoute || !activeRoute.legs || !activeRoute.geometry) return [];

    const coordinates = activeRoute.geometry.coordinates;
    if (!coordinates || coordinates.length === 0) return [];

    const points = [];
    let cumulativeDistance = 0;

    activeRoute.legs.forEach((leg) => {
      if (!leg.maneuvers || !Array.isArray(leg.maneuvers)) return;

      leg.maneuvers.forEach((maneuver) => {
        // Skip arrive (type 4-6) maneuvers — those are shown as endpoint markers
        const isArrive = maneuver.type >= 4 && maneuver.type <= 6;
        if (isArrive) return;

        let coord = null;
        if (maneuver.begin_shape_index !== undefined) {
          const idx = maneuver.begin_shape_index;
          if (idx >= 0 && idx < coordinates.length) {
            coord = coordinates[idx];
          }
        }

        if (!coord) return;

        // Distance shown = how far traveled to reach this point (before this step)
        points.push({
          lngLat: coord,
          instruction: maneuver.instruction || '',
          distance: cumulativeDistance,
          stepDistance: maneuver.length || 0,
          type: maneuver.type,
        });

        // Accumulate distance AFTER pushing (so first dot = 0, last dot = total - last step)
        cumulativeDistance += (maneuver.length || 0);
      });
    });

    return points;
  }, [getActiveRoute]);

  /**
   * Build GeoJSON FeatureCollection from maneuver points
   */
  const buildGeoJSON = useCallback((points) => {
    return {
      type: 'FeatureCollection',
      features: points.map((pt, idx) => ({
        type: 'Feature',
        id: idx,
        geometry: {
          type: 'Point',
          coordinates: pt.lngLat,
        },
        properties: {
          instruction: pt.instruction,
          distance: pt.distance,
          index: idx,
        },
      })),
    };
  }, []);

  /**
   * Show instruction card popup
   */
  const showPopup = useCallback((map, lngLat, instruction, distance) => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const distanceText = distance === 0 ? '0.0 km' : formatStepDistance(distance);

    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'route-step-popup',
      anchor: 'bottom',
      offset: [0, -8],
      maxWidth: '240px',
    });

    popup
      .setLngLat(lngLat)
      .setHTML(`
        <div class="route-step-popup-card">
          <div class="route-step-popup-instruction">${instruction?.replace(/\.$/, '')}</div>
          ${distanceText ? `<div class="route-step-popup-distance">Distance ${distanceText}</div>` : ''}
        </div>
      `)
      .addTo(map);

    popupRef.current = popup;
  }, []);

  /**
   * Hide popup
   */
  const hidePopup = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, []);

  /**
   * Remove layer, source, and event handlers from map
   */
  const cleanup = useCallback((map) => {
    if (!map) return;

    if (handlersRef.current) {
      try {
        map.off('mouseenter', LAYER_ID, handlersRef.current.mouseenter);
        map.off('mouseleave', LAYER_ID, handlersRef.current.mouseleave);
        map.off('click', LAYER_ID, handlersRef.current.click);
      } catch (e) {
        // Ignore
      }
      handlersRef.current = null;
    }

    try {
      if (map.getLayer && map.getLayer(LAYER_ID)) {
        map.removeLayer(LAYER_ID);
      }
      if (map.getSource && map.getSource(SOURCE_ID)) {
        map.removeSource(SOURCE_ID);
      }
    } catch (e) {
      // Ignore cleanup errors
    }
    hidePopup();
    hoveredIdRef.current = null;
  }, [hidePopup]);

  /**
   * Highlight a specific step dot by index — enlarges it and shows popup.
   * Called externally when hovering a step in the routing details panel.
   * Does NOT move the map.
   */
  const highlightStep = useCallback((stepIndex) => {
    const map = mapRef?.current;
    if (!map || !map.getSource(SOURCE_ID)) return;

    const points = pointsRef.current;
    if (stepIndex < 0 || stepIndex >= points.length) return;

    // Clear previous hover state
    if (hoveredIdRef.current !== null) {
      try {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
      } catch (e) { /* ignore */ }
    }

    // Set new hover state
    hoveredIdRef.current = stepIndex;
    try {
      map.setFeatureState({ source: SOURCE_ID, id: stepIndex }, { hover: true });
    } catch (e) { /* ignore */ }

    // Show popup
    const point = points[stepIndex];
    if (point) {
      showPopup(map, point.lngLat, point.instruction, point.distance);
    }
  }, [mapRef, showPopup]);

  /**
   * Focus on a specific step — enlarges dot, shows popup, AND centers map on it.
   * Called externally when clicking a step in the routing details panel.
   */
  const focusStep = useCallback((stepIndex) => {
    const map = mapRef?.current;
    if (!map || !map.getSource(SOURCE_ID)) return;

    const points = pointsRef.current;
    if (stepIndex < 0 || stepIndex >= points.length) return;

    // Clear previous hover state
    if (hoveredIdRef.current !== null) {
      try {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
      } catch (e) { /* ignore */ }
    }

    // Set new hover state
    hoveredIdRef.current = stepIndex;
    try {
      map.setFeatureState({ source: SOURCE_ID, id: stepIndex }, { hover: true });
    } catch (e) { /* ignore */ }

    // Show popup and center map
    const point = points[stepIndex];
    if (point) {
      showPopup(map, point.lngLat, point.instruction, point.distance);
      map.easeTo({
        center: point.lngLat,
        duration: 500,
      });
    }
  }, [mapRef, showPopup]);

  /**
   * Clear any highlighted step
   */
  const clearHighlight = useCallback(() => {
    const map = mapRef?.current;
    if (!map) return;

    if (hoveredIdRef.current !== null) {
      try {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
      } catch (e) { /* ignore */ }
      hoveredIdRef.current = null;
    }
    hidePopup();
  }, [mapRef, hidePopup]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    highlightStep,
    clearHighlight,
    focusStep,
  }), [highlightStep, clearHighlight, focusStep]);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    if (!routeDetails) {
      cleanup(map);
      return;
    }

    if (!map.getStyle || !map.getStyle()) {
      cleanup(map);
      return;
    }

    const points = getManeuverPoints();
    pointsRef.current = points;

    if (points.length === 0) {
      cleanup(map);
      return;
    }

    const geojson = buildGeoJSON(points);

    // If source already exists, just update data
    if (map.getSource(SOURCE_ID)) {
      map.getSource(SOURCE_ID).setData(geojson);
      return;
    }

    // Add source
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: geojson,
    });

    // Add circle layer — GPU-rendered, stays perfectly fixed on the route
    // Figma: 6x6px circle, fill white (#FFF), stroke 1px #4E6FD8
    map.addLayer({
      id: LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          4.5,
          3,
        ],
        'circle-color': '#ffffff',
        'circle-stroke-width': 1,
        'circle-stroke-color': '#4E6FD8',
        'circle-opacity': 1,
      },
    });

    // Hover interaction
    const onMouseEnter = (e) => {
      map.getCanvas().style.cursor = 'pointer';
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (hoveredIdRef.current !== null) {
          map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
        }
        hoveredIdRef.current = feature.id;
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: true });
        const coords = feature.geometry.coordinates.slice();
        const { instruction, distance } = feature.properties;
        showPopup(map, coords, instruction, distance);
      }
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
      if (hoveredIdRef.current !== null) {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
        hoveredIdRef.current = null;
      }
      hidePopup();
    };

    const onClick = (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (hoveredIdRef.current !== null) {
          map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
        }
        hoveredIdRef.current = feature.id;
        map.setFeatureState({ source: SOURCE_ID, id: hoveredIdRef.current }, { hover: true });
        const coords = feature.geometry.coordinates.slice();
        const { instruction, distance } = feature.properties;
        showPopup(map, coords, instruction, distance);
      }
    };

    map.on('mouseenter', LAYER_ID, onMouseEnter);
    map.on('mouseleave', LAYER_ID, onMouseLeave);
    map.on('click', LAYER_ID, onClick);

    handlersRef.current = {
      mouseenter: onMouseEnter,
      mouseleave: onMouseLeave,
      click: onClick,
    };

    return () => {
      cleanup(map);
    };
  }, [mapRef, routeDetails, selectedRouteIndex, getManeuverPoints, buildGeoJSON, showPopup, hidePopup, cleanup]);

  return null;
});

export default RouteStepMarkers;
